import { useState, useEffect, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  PlusIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Button, Input, Textarea } from "components/ui";
import clsx from "clsx";
import {
  getAllQueryCategories,
  createQuery,
  getAllLeads,
} from "services/queryService";
import { toast } from "sonner"; // Add Sonner toast import

// Status and Priority options
const STATUS_OPTIONS = [
  { id: "open", label: "Open", value: "open" },
  { id: "closed", label: "Closed", value: "closed" },
];

const PRIORITY_OPTIONS = [
  { id: "normal", label: "Normal", value: "normal" },
  { id: "medium", label: "Medium", value: "medium" },
  { id: "high", label: "High", value: "high" },
];

export function QueryCreationModal({
  isOpen,
  onClose,
  onSuccess,
  preSelectedLeadId = null,
  restrictLeadSelection = false,
}) {
  const [formData, setFormData] = useState({
    leadId: preSelectedLeadId || "",
    categoryId: "",
    subCategoryId: "",
    subject: "",
    description: "",
    priority: "normal",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leads, setLeads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchTerms, setSearchTerms] = useState({});
  const [apiError, setApiError] = useState("");
  const [leadsError, setLeadsError] = useState(""); // New state for leads-specific errors
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  // Fetch leads using the new service
  useEffect(() => {
    const fetchLeads = async () => {
      setLoadingLeads(true);
      setLeadsError("");
      try {
        const result = await getAllLeads();
        if (result.success && result.data) {
          // Transform the leads data to match dropdown format
          const leadOptions = result.data.map((lead) => ({
            id: lead.Id,
            label: `${lead.LeadName} - ${lead.Email}`,
            value: lead.Id,
            leadName: lead.LeadName,
            email: lead.Email,
          }));
          setLeads(leadOptions);
        } else {
          setLeadsError(result.error || "Failed to load leads");
          setLeads([]);
        }
      } catch (error) {
        console.error("Error fetching leads:", error);
        setLeadsError("An unexpected error occurred while loading leads");
        setLeads([]);
      } finally {
        setLoadingLeads(false);
      }
    };

    if (isOpen) {
      fetchLeads();
    }
  }, [isOpen]);

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const result = await getAllQueryCategories();
        if (result.success && result.data) {
          const categoryOptions = result.data.map((cat) => ({
            id: cat.CategoryId,
            label: cat.CategoryName,
            value: cat.CategoryId,
            subCategories: cat.SubCategories || [],
          }));
          setCategories(categoryOptions);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoadingCategories(false);
      }
    };

    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  // Update subcategories when category changes
  useEffect(() => {
    if (formData.categoryId) {
      const selectedCategory = categories.find(
        (cat) => cat.value === formData.categoryId,
      );
      if (selectedCategory && selectedCategory.subCategories) {
        const subCategoryOptions = selectedCategory.subCategories.map(
          (subCat) => ({
            id: subCat.CategoryId,
            label: subCat.CategoryName,
            value: subCat.CategoryId,
          }),
        );
        setSubCategories(subCategoryOptions);
      } else {
        setSubCategories([]);
      }
    } else {
      setSubCategories([]);
      setFormData((prev) => ({ ...prev, subCategoryId: "" }));
    }
  }, [formData.categoryId, categories]);

  // Reset form when modal opens - but preserve preSelectedLeadId
  useEffect(() => {
    if (isOpen) {
      setFormData({
        leadId: preSelectedLeadId || "",
        categoryId: "",
        subCategoryId: "",
        subject: "",
        description: "",
        priority: "normal",
      });
      setErrors({});
      setApiError("");
      setLeadsError("");
      setOpenDropdown(null);
      setSearchTerms({});
      setIsFormSubmitted(false);
    }
  }, [isOpen, preSelectedLeadId]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (apiError) {
      setApiError("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.leadId) newErrors.leadId = "Lead selection is required";
    if (!formData.categoryId) newErrors.categoryId = "Category is required";

    // MANDATORY SUBCATEGORY VALIDATION
    if (!formData.subCategoryId) {
      newErrors.subCategoryId = "Subcategory selection is mandatory";
    }

    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (!formData.priority) newErrors.priority = "Priority is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Show validation error toast
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    setIsSubmitting(true);
    setApiError("");

    try {
      // Prepare payload with subcategory ID as categoryId (as per requirement)
      const queryPayload = {
        leadId: formData.leadId,
        categoryId: formData.subCategoryId, // Use subcategory ID as categoryId
        subject: formData.subject,
        description: formData.description,
        status: "open",
        priority: formData.priority,
      };

      const result = await createQuery(queryPayload);

      if (result.success) {
        // Show success toast
        toast.success("Query created successfully!", {
          description: `Query "${formData.subject}" has been created and assigned.`,
        });

        // Mark form as successfully submitted
        setIsFormSubmitted(true);

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
      } else {
        const errorMessage = result.error || "Failed to create query";
        setApiError(errorMessage);
        // Show error toast
        toast.error("Failed to create query", {
          description: errorMessage,
        });
      }
    } catch (error) {
      console.error("Error creating query:", error);
      const errorMessage = "An unexpected error occurred. Please try again.";
      setApiError(errorMessage);
      // Show error toast
      toast.error("Unexpected Error", {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDropdown = (dropdownKey) => {
    setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey);
  };

  const getFilteredOptions = (options, searchTerm) => {
    if (!searchTerm) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  // Render lead dropdown with restriction support
  const renderLeadDropdown = () => {
    if (restrictLeadSelection && preSelectedLeadId) {
      const selectedLead = leads.find(
        (lead) => lead.value === preSelectedLeadId,
      );
      return (
        <div className="space-y-2">
          <label className="mb-2 text-sm text-gray-700 dark:text-gray-300">
            Lead Selection *
          </label>
          <div className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400">
            {selectedLead ? selectedLead.label : "Loading selected lead..."}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Lead is pre-selected and cannot be changed
          </p>
        </div>
      );
    }

    // Regular dropdown for unrestricted selection
    return (
      <div className="space-y-2">
        <label className="text-sm text-gray-700 dark:text-gray-300">
          Lead Selection *
        </label>
        {renderDropdown(
          "leadId",
          leads,
          formData.leadId,
          leads.length === 0 && !loadingLeads
            ? "No leads available"
            : "Select a lead",
          loadingLeads,
          !!leadsError,
        )}
        {errors.leadId && (
          <p className="text-error-600 dark:text-error-400 text-sm">
            {errors.leadId}
          </p>
        )}
      </div>
    );
  };

  const renderDropdown = (
    dropdownKey,
    options,
    selectedValue,
    placeholder = "Select...",
    isLoading = false,
    hasError = false,
  ) => {
    const isOpen = openDropdown === dropdownKey;
    const searchTerm = searchTerms[dropdownKey] || "";
    const filteredOptions = getFilteredOptions(options, searchTerm);
    const selectedOption = options?.find((opt) => opt.value === selectedValue);
    const fieldError = errors[dropdownKey];

    return (
      <div className="relative mt-1">
        <Button
          variant="outlined"
          className={clsx(
            "flex w-full items-center justify-between gap-2 border-gray-300 bg-white px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700",
            selectedValue &&
            "border-primary-500 bg-primary-50 dark:bg-primary-900/20",
            (fieldError || hasError) &&
            "border-error-500 bg-error-50 dark:border-error-400 dark:bg-error-900/20",
          )}
          onClick={() => toggleDropdown(dropdownKey)}
          disabled={
            isLoading ||
            (dropdownKey === "subCategoryId" && !formData.categoryId)
          }
          type="button"
        >
          <span
            className={clsx(
              "truncate text-left",
              selectedValue
                ? "text-primary-700 dark:text-primary-300 font-medium"
                : "text-gray-500 dark:text-gray-400",
              (fieldError || hasError) && "text-error-700 dark:text-error-300",
            )}
          >
            {isLoading ? "Loading..." : selectedOption?.label || placeholder}
          </span>
          <ChevronDownIcon
            className={clsx(
              "size-4 transition-transform",
              isOpen && "rotate-180",
            )}
          />
        </Button>

        {isOpen && !isLoading && (
          <div className="absolute z-50 mt-1 max-h-64 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
            {options && options.length > 5 && (
              <div className="border-b border-gray-200 p-2 dark:border-gray-600">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="focus:ring-primary-500 w-full rounded-md border border-gray-200 bg-gray-50 py-2 pr-3 pl-9 text-sm text-gray-900 focus:ring-2 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerms((prev) => ({
                        ...prev,
                        [dropdownKey]: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
            )}

            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={clsx(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700",
                    selectedValue === option.value &&
                    "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300",
                  )}
                  onClick={() => {
                    handleInputChange(dropdownKey, option.value);
                    setOpenDropdown(null);
                  }}
                >
                  {selectedValue === option.value && (
                    <CheckIcon className="text-primary-600 size-4" />
                  )}
                  <span
                    className={selectedValue === option.value ? "ml-0" : "ml-6"}
                  >
                    {option.label}
                  </span>
                </button>
              ))}

              {filteredOptions.length === 0 && searchTerm && (
                <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                  `No options found for ${searchTerm}`
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[1050]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity dark:bg-gray-900/60" />
        </TransitionChild>

        <div className="fixed inset-0 z-[1051] flex items-center justify-center p-4 overflow-y-auto">
          <TransitionChild
            as={DialogPanel}
            enter="ease-out transform-gpu transition-transform duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in transform-gpu transition-transform duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
            className="w-full max-w-xs transform-gpu rounded-xl bg-white shadow-xl transition-all sm:max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-3xl dark:bg-gray-800"
          >
            {/* Enhanced responsive modal container content */}
            <div className="flex max-h-[90vh] flex-col overflow-hidden rounded-xl">
              {/* Header - responsive padding and text sizes */}
              <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-3 py-3 sm:px-6 sm:py-4 dark:border-gray-600">
                <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                  <div className="bg-primary-100 dark:bg-primary-900/20 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full sm:h-10 sm:w-10">
                    <PlusIcon className="text-primary-600 dark:text-primary-400 size-4 sm:size-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-gray-900 sm:text-lg dark:text-gray-100">
                      Create New Query
                    </h2>
                    <p className="truncate text-xs text-gray-500 sm:text-sm dark:text-gray-400">
                      Fill in the details to create a new query
                    </p>
                  </div>
                </div>
                <Button
                  variant="flat"
                  isIcon
                  onClick={onClose}
                  className="size-7 flex-shrink-0 sm:size-8"
                  disabled={isSubmitting}
                >
                  <XMarkIcon className="size-4 sm:size-5" />
                </Button>
              </div>

              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto">
                {/* API Error Display */}
                {apiError && (
                  <div className="bg-error-50 border-error-200 dark:bg-error-900/20 dark:border-error-800 mx-3 mt-3 rounded-lg border p-3 sm:mx-6 sm:mt-4 sm:p-4">
                    <div className="flex items-start gap-2">
                      <ExclamationTriangleIcon className="text-error-600 dark:text-error-400 mt-0.5 size-4 flex-shrink-0 sm:size-5" />
                      <p className="text-error-700 dark:text-error-300 text-xs sm:text-sm">
                        {apiError}
                      </p>
                    </div>
                  </div>
                )}

                {/* Leads Error Display */}
                {leadsError && (
                  <div className="bg-warning-50 border-warning-200 dark:bg-warning-900/20 dark:border-warning-800 mx-3 mt-3 rounded-lg border p-3 sm:mx-6 sm:mt-4 sm:p-4">
                    <div className="flex items-start gap-2">
                      <ExclamationTriangleIcon className="text-warning-600 dark:text-warning-400 mt-0.5 size-4 flex-shrink-0 sm:size-5" />
                      <p className="text-warning-700 dark:text-warning-300 text-xs sm:text-sm">
                        Failed to load leads: {leadsError}
                      </p>
                    </div>
                  </div>
                )}

                {/* Form - responsive padding */}
                <form onSubmit={handleSubmit} className="p-3 sm:p-6">
                  <div className="space-y-4 sm:space-y-6">
                    {/* Lead Selection */}
                    {renderLeadDropdown()}

                    {/* Category and Subcategory - responsive grid */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-sm text-gray-700 sm:text-sm dark:text-gray-300">
                          Category *
                        </label>
                        {renderDropdown(
                          "categoryId",
                          categories,
                          formData.categoryId,
                          "Select category",
                          loadingCategories,
                        )}
                        {errors.categoryId && (
                          <p className="text-error-600 dark:text-error-400 text-xs sm:text-sm">
                            {errors.categoryId}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm text-gray-700 sm:text-sm dark:text-gray-300">
                          Subcategory *
                        </label>
                        {renderDropdown(
                          "subCategoryId",
                          subCategories,
                          formData.subCategoryId,
                          formData.categoryId
                            ? "Select subcategory"
                            : "Select category first",
                          false,
                        )}
                        {errors.subCategoryId && (
                          <p className="text-error-600 dark:text-error-400 text-xs sm:text-sm">
                            {errors.subCategoryId}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Subject */}
                    <div className="space-y-2">
                      <Input
                        label="Subject *"
                        value={formData.subject}
                        onChange={(e) =>
                          handleInputChange("subject", e.target.value)
                        }
                        error={errors.subject}
                        placeholder="Enter query subject"
                        className="w-full text-sm"
                      />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Textarea
                        label="Description *"
                        value={formData.description}
                        onChange={(e) =>
                          handleInputChange("description", e.target.value)
                        }
                        error={errors.description}
                        placeholder="Enter detailed description of the query"
                        rows={3}
                        className="w-full resize-none text-sm"
                      />
                    </div>

                    {/* Status and Priority - responsive grid */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Input
                          label="Status *"
                          disabled={true}
                          value={formData?.status}
                          onChange={(e) =>
                            handleInputChange("status", e.target.value)
                          }
                          error={errors.status}
                          placeholder="Open"
                          className="w-full text-sm"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm text-gray-700 sm:text-sm dark:text-gray-300">
                          Priority *
                        </label>
                        {renderDropdown(
                          "priority",
                          PRIORITY_OPTIONS,
                          formData.priority,
                          "Select priority",
                        )}
                        {errors.priority && (
                          <p className="text-error-600 dark:text-error-400 text-xs sm:text-sm">
                            {errors.priority}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Footer - responsive padding and button sizes */}
              <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-600">
                <div className="flex flex-col items-stretch justify-end gap-2 p-3 sm:flex-row sm:items-center sm:gap-3 sm:p-6">
                  <Button
                    variant="outlined"
                    color="neutral"
                    onClick={onClose}
                    disabled={isSubmitting}
                    className="w-full px-4 py-2 text-sm sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="solid"
                    color="primary"
                    disabled={isSubmitting || leads.length === 0}
                    onClick={handleSubmit}
                    className="w-full px-4 py-2 text-sm sm:w-auto"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="mr-2 size-3 animate-spin rounded-full border-2 border-white border-t-transparent sm:size-4" />
                        <span className="text-xs sm:text-sm">Creating...</span>
                      </>
                    ) : (
                      <>
                        <PlusIcon className="mr-2 size-3 sm:size-4" />
                        <span className="text-xs sm:text-sm">Create Query</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

          </TransitionChild>
        </div>
      </Dialog >
    </Transition >
  );
}
